import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";
import { track, trackPurchase } from "../tracking";
import { setupRecaptcha } from "helpers/recaptcha";
import { logger } from "../datadog";

export default class extends Controller {
  static outlets = ["payment-update"];
  static targets = [
    "anonymousId",
    "checkoutError",
    "checkoutFormId",
    "checkoutSuccess",
    "email",
    "firstName",
    "googleClientId",
    "googleSessionId",
    "lastName",
    "licensePlate",
    "locationSelect",
    "parentUrl",
    "phone",
    "product",
    "quantity",
    "recaptchaDiv",
    "recaptchaInput",
    "slowMessage",
    "subtotal",
    "tax",
    "taxEstimate",
    "tosContent",
    "total",
    "totalWithTax",
  ];

  static values = {
    customerAppRefreshEnabled: Boolean,
    products: Array,
    recaptchaEnabled: Boolean,
    recaptchaSiteKey: String,
    salespathAnyOrigin: Boolean,
    showTotalCost: Boolean,
    taxEstimateRequiresPii: Boolean,
    taxForSalesEnabled: Boolean,
    tosContentPath: String,
  };

  connect() {
    this.estimateTax();
    if (
      this.productsValue.length === 1 &&
      !this.taxForSalesEnabledValue &&
      this.showTotalCostValue
    ) {
      this.updateTotal();
    }

    this.debouncedEstimateTax = debounce(this.estimateTax.bind(this), 1500);
    this.debouncedTrackEmailEntered = debounce(this.trackEmailEntered.bind(this), 500);
    this.setAnonymousId();
    this.paymentUpdateOutlet.onFormSubmit(this.startSlowMessageCountdown.bind(this));
    this.invalidateQuantityInputs();
    if (this.recaptchaEnabledValue) {
      console.log("ReCAPTCHA is enabled - connecting...");
      this.setupRecaptcha();
    }
    this.boundHandleMessage = this.handleMessage.bind(this);
    window.addEventListener("message", this.boundHandleMessage);

    this.errorListeners = [];
    this.setGoogleAnalyticsValues();
  }

  disconnect() {
    window.removeEventListener("message", this.boundHandleMessage);
    this.errorListeners = [];
  }

  addErrorListener(listener) {
    this.errorListeners.push(listener);
  }

  setupRecaptcha() {
    console.log("Calling setupRecaptcha in checkout_forms Stimulus controller");

    const executeRecaptcha = setupRecaptcha(this.recaptchaDivTarget, this.recaptchaSiteKeyValue);
    this.paymentUpdateOutlet.addAsyncSubmitInterrupt(async () => {
      console.log("Adding ReCAPTCHA submit interrupt...");
      this.recaptchaInputTarget.value = await executeRecaptcha();
      return true;
    });
  }

  invalidateQuantityInputs() {
    if (this.hasQuantityTarget) {
      this.quantityTargets[0].setCustomValidity("At least one product is required.");
    }
  }

  setGoogleAnalyticsValues() {
    if (typeof gtag !== "undefined") {
      // the "target" param doesn't seem to require an actual Google measurement ID
      const target = "G-XXXXXXXXXX";
      gtag("get", target, "client_id", (val) => {
        this.googleClientIdTarget.value = val;
      });
      gtag("get", target, "session_id", (val) => {
        this.googleSessionIdTarget.value = val;
      });
    }
  }

  checkoutSuccessTargetConnected(target) {
    const parent = target.dataset.parent;
    const url = target.dataset.url;
    const {
      purchaseOrderId,
      purchaseOrderEid,
      salesOrderTax,
      salesOrderShipping,
      salesOrderValue,
      purchaseSource,
      salespathCustomer,
      salespathProductName,
      salespathVoucher,
      salespathSaleId,
      salespathLoyaltyEnroll,
      serverSideGoogleAnalytics,
    } = target.dataset;

    if (salespathCustomer) {
      window.parent.postMessage(
        {
          message: "checkoutSuccess",
          customerName: salespathCustomer,
          productName: salespathProductName,
          voucherBarcode: salespathVoucher,
          posSaleId: salespathSaleId,
          loyaltyEnrollment: salespathLoyaltyEnroll === "true",
        },
        this.salespathAnyOriginValue ? "*" : "https://app.ignitewash.com"
      );
    }

    const callback = () => {
      if (purchaseSource === "loyalty_program_reward_redemption") {
        if (this.customerAppRefreshEnabledValue) {
          window.location.href = `/portal/loyalty_programs/checkout_forms/success/${purchaseOrderId}`;
        } else {
          Turbo.visit(url, { frame: "checkout_form_confirmation" });
        }
      } else if (parent && parent !== "false") {
        window.top.location.href = url;
      } else {
        window.location.href = url;
      }
    };

    try {
      if (typeof gtag === "undefined") {
        this.log("google gtag undefined", { rinsed: { purchaseOrderEid } });
      } else {
        this.log("google gtag defined", { rinsed: { purchaseOrderEid } });
        gtag("get", "G-XXXXXXXXXX", "client_id", (clientId) => {
          this.log("google gtag client_id", { rinsed: { purchaseOrderEid, clientId } });
        });
        gtag("get", "G-XXXXXXXXXX", "session_id", (sessionId) => {
          this.log("google gtag session_id", { rinsed: { purchaseOrderEid, sessionId } });
        });
      }
    } catch (error) {
      this.log("google gtag log error", { error: error.stack });
    }

    trackPurchase(
      {
        transaction_id: purchaseOrderId,
        value: salesOrderValue,
        tax: salesOrderTax,
        shipping: salesOrderShipping,
        currency: "USD",
        purchase_source: purchaseSource,
      },
      serverSideGoogleAnalytics,
      callback
    );
  }

  checkoutErrorTargetConnected(target) {
    const { salespathCustomer, salespathError } = target.dataset;

    if (salespathCustomer) {
      window.parent.postMessage(
        {
          message: "checkoutError",
          customerName: salespathCustomer,
          errorMessage: salespathError,
        },
        this.salespathAnyOriginValue ? "*" : "https://app.ignitewash.com"
      );
    }
    this.cancelSlowMessage();
    this.hideSlowMessage();
    this.paymentUpdateOutlet.hideSpinner();
    this.paymentUpdateOutlet.enableSubmit();

    this.errorListeners.forEach((listener) => listener(target));
  }

  startSlowMessageCountdown() {
    if (!this.slowMessageTimeoutId) {
      this.slowMessageTimeoutId = setTimeout(() => {
        this.slowMessageTarget.hidden = false;
      }, 15000); // 15 seconds
    }
  }

  cancelSlowMessage() {
    if (this.slowMessageTimeoutId) {
      clearTimeout(this.slowMessageTimeoutId);
      this.slowMessageTimeoutId = null;
    }
  }

  hideSlowMessage() {
    this.slowMessageTarget.hidden = true;
  }

  setAnonymousId() {
    this.anonymousIdTarget.value = document.querySelector("body").dataset.anonymous_id;
  }

  updateTos() {
    const productId = this.getSelectedProductId();
    if (!productId || !this.hasTosContentTarget) {
      return;
    }

    if (this.tosContentPathValue) {
      Turbo.visit(`${this.tosContentPathValue}?product_id=${productId}`, { frame: "tos_content" });
    }
  }

  updateTotal() {
    let total = 0;

    if (this.hasQuantityTarget) {
      this.quantityTargets.forEach((quantityTarget) => {
        const quantity = parseInt(quantityTarget.value) || 0;
        const price = parseFloat(quantityTarget.dataset.price) || 0;
        total += (quantity * price) / 100;
      });
    } else {
      this.productTargets.forEach((productTarget) => {
        if (productTarget.checked) {
          const price = parseFloat(productTarget.dataset.price) || 0;
          total += price / 100;
        }
      });
    }

    this.totalTarget.textContent = total.toFixed(2);
  }

  checkQuantities() {
    if (this.quantityTargets.some((input) => parseInt(input.value ?? 0) > 0)) {
      this.quantityTargets[0].setCustomValidity("");
    } else {
      this.quantityTargets[0].setCustomValidity("At least one product is required.");
    }
  }

  getSelectedProductId() {
    for (const product of this.productTargets) {
      if (product.checked) {
        return product.value;
      }
    }
    return null;
  }

  async estimateTax() {
    if (!this.taxForSalesEnabledValue) {
      return;
    }

    const selectedProductId = this.getSelectedProductId();

    if (this.shouldEstimateTax(selectedProductId)) {
      const { subtotal, tax, total } = await this.fetchTaxEstimate({
        customer_email: this.emailTarget.value,
        customer_first_name: this.firstNameTarget.value,
        customer_last_name: this.lastNameTarget.value,
        customer_phone: this.phoneTarget.value,
        membership_license_plate: this.hasLicensePlateTarget ? this.licensePlateTarget.value : "",
        product_id: selectedProductId,
        wash_connect_site_id: this.hasLocationSelectTarget ? this.locationSelectTarget.value : "",
      });

      this.subtotalTarget.textContent = subtotal;
      this.totalWithTaxTarget.textContent = total;
      this.taxTarget.textContent = tax;

      this.taxEstimateTarget.hidden = false;
    } else {
      this.taxEstimateTarget.hidden = true;
    }
  }

  async trackEmailEntered() {
    if (!this.emailTarget.value?.trim()) return;

    const payload = {
      checkout_form_id: this.checkoutFormIdTarget.value,
      email: this.emailTarget.value,
      first_name: this.firstNameTarget.value,
      last_name: this.lastNameTarget.value,
      parent_url: this.parentUrlTarget.value,
      product_id: this.getSelectedProductId(),
    };

    track("Email entered in checkout form", payload);
  }

  shouldEstimateTax(selectedProductId) {
    if (!selectedProductId || selectedProductId === "") {
      return false;
    }

    if (this.taxEstimateRequiresPiiValue) {
      return (
        this.emailTarget.value !== "" &&
        this.firstNameTarget.value !== "" &&
        this.lastNameTarget.value !== "" &&
        this.phoneTarget.value !== "" &&
        (!this.hasLocationSelectTarget || this.locationSelectTarget.value !== "")
      );
    }

    return true;
  }

  async fetchTaxEstimate(payload) {
    const response = await fetch("/taxes/estimate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    return await response.json();
  }

  handleMessage(event) {
    if (
      event.data?.action &&
      ["focusFirstName", "disableSubmit", "enableSubmit"].includes(event.data.action)
    ) {
      this.log("CheckoutFormsController.handleMessage", event);

      const allowedOrigin = this.salespathAnyOriginValue ? "*" : "https://app.ignitewash.com";

      this.log("salespathData", {
        salespathAnyOrigin: this.salespathAnyOriginValue,
        allowedOrigin,
      });

      if (allowedOrigin !== "*" && event.origin !== allowedOrigin) {
        this.log("handleMessage: ignoring message from unknown origin");
        return;
      }

      if (event.data.action === "focusFirstName") {
        this.firstNameTarget.focus();
      } else if (event.data.action === "disableSubmit") {
        this.paymentUpdateOutlet.disableSubmit();
      } else if (event.data.action === "enableSubmit") {
        this.paymentUpdateOutlet.enableSubmit();
      }
    }
  }

  log(message, details) {
    try {
      logger.log(message, details);
    } catch (error) {
      try {
        logger.log("error logging");
      } catch (error2) {}
    }
  }
}
